import { useCallback, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import useSWR from 'swr'
import { asPipelineId, type PipelineId } from '@ludus/core'
import { Badge } from '@ludus/ui'
import { FlowApiError, fetchFlow, type FlowDetail, type FlowTrigger } from '@/lib/api/flows.ts'
import { fetchPipeline, type PipelineApiError } from '@/lib/api/pipelines.ts'
import { Canvas } from '@/features/flows/Canvas/Canvas.tsx'
import { RunsPanel } from '@/features/flows/RunsPanel/RunsPanel.tsx'
import { Breadcrumb } from '@/shared/breadcrumb/Breadcrumb.tsx'
import { CenteredPanel } from '@/shared/feedback/CenteredPanel.tsx'

/**
 * One flow, full width — the route the catalogue card links to
 * (`/pipelines/:pipelineId/flows/:flowId`, D9). It resolves the URL, gates on
 * the two reads the way `PipelineSettingsPage` does, and composes the canvas
 * (#174) with the runs panel (#176).
 *
 * **It owns the one piece of state the two share**: which run is open, and the
 * nodes that run touched. The panel decides the first and its step log supplies
 * the second; the canvas only draws what it is handed. Holding it here is what
 * keeps "what ran" and "what is configured" one picture without either panel
 * reaching into the other.
 *
 * Self-contained like the lead detail and the settings page — `useParams` plus
 * its own lean fetches — so it does not mount `PipelineIdContext`. The flow key
 * is the one `FlowCard` already read, so arriving from the catalogue renders
 * from cache and revalidates.
 *
 * No nav change: the sidebar lights an item on a path prefix
 * (`SidebarNavItem`), and this route is under `/pipelines`.
 */
export function FlowPage() {
  const { t } = useTranslation()
  const params = useParams<{ pipelineId: string; flowId: string }>()

  const pipelineId = params.pipelineId ? asPipelineId(params.pipelineId) : null
  const flowId = params.flowId ?? null

  const flowSwr = useSWR<{ flow: FlowDetail; triggers: FlowTrigger[] }, FlowApiError>(
    pipelineId && flowId ? `/pipelines/${pipelineId}/flows/${flowId}` : null,
    () => fetchFlow(pipelineId as PipelineId, flowId as string),
  )
  const pipelineSwr = useSWR(pipelineId ? `/pipelines/${pipelineId}` : null, () =>
    fetchPipeline(pipelineId as PipelineId),
  )

  // Which run is open, and which nodes it touched. Both live here rather than
  // in either panel: the runs list decides the first, the run's own step log
  // supplies the second, and the canvas only draws what it is handed — so
  // neither panel has to reach into the other.
  const [selectedRunId, setSelectedRunId] = useState<string | null>(null)
  const [runPath, setRunPath] = useState<readonly string[]>([])
  const onTouchedNodes = useCallback((nodeIds: readonly string[]) => setRunPath(nodeIds), [])

  if (!pipelineId || !flowId) {
    return (
      <CenteredPanel
        title={t('pages.flows.invalid.title', { defaultValue: 'Invalid URL' })}
        body={t('pages.flows.invalid.body', {
          defaultValue: 'The flow URL is missing a pipeline or a flow id.',
        })}
      />
    )
  }

  if (flowSwr.isLoading || pipelineSwr.isLoading) {
    return (
      <CenteredPanel title={t('pages.flows.loading', { defaultValue: 'Loading flow…' })} muted />
    )
  }

  const error =
    (flowSwr.error as FlowApiError | undefined) ??
    (pipelineSwr.error as PipelineApiError | undefined)
  if (error) {
    return (
      <CenteredPanel
        title={t('pages.flows.error.title', { defaultValue: 'Could not load flow' })}
        body={
          error.kind === 'not_found'
            ? t('pages.flows.error.notFound', {
                defaultValue: 'This flow is no longer available in this pipeline.',
              })
            : t('pages.flows.error.generic', {
                defaultValue: 'The engine returned an error while loading this flow.',
              })
        }
        meta={error.status !== null ? `HTTP ${error.status} · ${error.message}` : undefined}
        tone="error"
      />
    )
  }

  if (!flowSwr.data || !pipelineSwr.data) return null

  const { flow } = flowSwr.data
  const pipeline = pipelineSwr.data

  return (
    <div className="flex w-full flex-1 flex-col gap-4 p-4 sm:p-6">
      <Breadcrumb
        items={[
          {
            label: t('pages.flows.breadcrumb.pipelines', { defaultValue: 'Pipelines' }),
            to: '/pipelines',
          },
          { label: pipeline.name, to: `/pipelines/${pipeline.id}` },
          {
            label: t('pages.flows.breadcrumb.settings', { defaultValue: 'Settings' }),
            to: `/pipelines/${pipeline.id}/settings`,
          },
          { label: flow.name },
        ]}
      />

      <header className="flex flex-wrap items-center gap-3 rounded-xl border border-border bg-background-secondary p-4">
        <div className="flex min-w-0 flex-1 flex-col gap-0.5">
          <div className="flex items-center gap-2">
            <h1 className="truncate text-xl font-semibold text-foreground">{flow.name}</h1>
            <Badge tone={flow.enabled ? 'primary' : 'muted'} size="sm">
              {flow.enabled
                ? t('pages.flows.on', { defaultValue: 'On' })
                : t('pages.flows.off', { defaultValue: 'Off' })}
            </Badge>
          </div>
          <p className="text-xs text-muted">
            {t('pages.flows.subtitle', {
              version: flow.version,
              defaultValue: 'Saved version {{version}}. Saving and enabling are separate acts.',
            })}
          </p>
        </div>
      </header>

      <div className="flex flex-1 flex-col gap-4 lg:flex-row">
        <Canvas pipelineId={pipelineId} flow={flow} flowSwr={flowSwr} highlight={runPath} />
        <RunsPanel
          pipelineId={pipelineId}
          flowId={flow.id}
          graph={flow.graph}
          selectedRunId={selectedRunId}
          onSelectRun={setSelectedRunId}
          onTouchedNodes={onTouchedNodes}
        />
      </div>
    </div>
  )
}
